import {apiGet} from './client'
import {formatDate, parseDate, type DateVal} from './gramps'

/** Событие «работа» в Gramps — EventType.OCCUPATION. */
export const EVENT_OCCUPATION = 37

/** Предприятия отдельным полем в Gramps нет. Кладём его атрибутом события
 *  с пользовательским типом: так запись разбирается обратно на два поля
 *  и остаётся читаемой в штатном интерфейсе. */
export const ATTR_EMPLOYER = 'Предприятие'

export interface JobEntry {
  /** handle события; null — запись ещё не сохранялась */
  handle: string | null
  profession: string
  employer: string
  from: string
  to: string
}

export const emptyJob = (): JobEntry => ({handle: null, profession: '', employer: '', from: '', to: ''})

export const jobIsEmpty = (j: JobEntry) =>
  !j.profession.trim() && !j.employer.trim() && !j.from.trim() && !j.to.trim()

/* ВАЖНО: в ответах GET типы приходят СТРОКАМИ («Occupation», «Birth»),
 * хотя на запись принимаются и строкой, и объектом {_class, value}.
 * Сравнение вида type.value === 37 не срабатывало никогда — запись
 * сохранялась, но нигде не показывалась. Локаль в запросы событий
 * не передаём, поэтому строка английская. */
export const isOccupation = (t: unknown): boolean =>
  typeof t === "string" ? t === "Occupation" : (t as {value?: number} | undefined)?.value === EVENT_OCCUPATION

/** Имя типа атрибута: тоже приходит строкой, а не {string: …}. */
export const attrTypeName = (t: unknown): string =>
  typeof t === "string" ? t : (t as {string?: string} | undefined)?.string || ""

interface RawEvent {
  handle: string
  type?: string | {value?: number}
  description?: string
  date?: {modifier?: number; dateval?: number[]}
  attribute_list?: {type?: string | {string?: string}; value?: string}[]
  [k: string]: unknown
}

/** Дата события «работа» -> два поля формы. Период (MOD_SPAN) кодируется
 *  восьмёркой значений: [д,м,г,slash, д2,м2,г2,slash2]. */
export function jobDatesFromEvent(ev?: RawEvent): {from: string; to: string} {
  const d = ev?.date
  const dv = d?.dateval || []
  if (!dv.length) return {from: '', to: ''}
  if (d?.modifier === 5 && dv.length >= 8) {
    return {from: formatDate(dv.slice(0, 4)), to: formatDate(dv.slice(4, 8))}
  }
  return {from: formatDate(dv.slice(0, 4)), to: ''}
}

export function jobFromEvent(ev: RawEvent): JobEntry {
  const empl = (ev.attribute_list || []).find(a => attrTypeName(a.type) === ATTR_EMPLOYER)
  return {
    handle: ev.handle,
    profession: ev.description || '',
    employer: empl?.value || '',
    ...jobDatesFromEvent(ev),
  }
}

/** Собираем поле date события из «с» и «по». Оба необязательны. */
export function jobDate(from: string, to: string) {
  const a = parseDate(from)
  const b = parseDate(to)
  const zero: DateVal = [0, 0, 0, false]
  const base = {_class: 'Date', calendar: 0, quality: 0, text: '', sortval: 0, newyear: 0}

  if (a && b) return {...base, modifier: 5, dateval: [...a, ...b]}
  // известен только один год — пишем обычной датой, а не открытым периодом:
  // «после 1955» и «с 1955» это разные утверждения, врать не будем
  if (a) return {...base, modifier: 0, dateval: a}
  if (b) return {...base, modifier: 0, dateval: b}
  return {...base, modifier: 0, dateval: zero}
}

export function jobBadDates(j: JobEntry): boolean {
  return (!!j.from.trim() && !parseDate(j.from)) || (!!j.to.trim() && !parseDate(j.to))
}

/** Загрузка мест работы человека: события типа Occupation из его event_ref_list. */
export async function loadJobs(eventHandles: string[]): Promise<JobEntry[]> {
  const evs = await Promise.all(eventHandles.map(h => apiGet<RawEvent>(`/events/${h}`)))
  return evs.filter(e => isOccupation(e.type)).map(jobFromEvent)
}

/** Тело события «работа». БЕЗ handle и gramps_id: их назначает сервер —
 *  проверено, POST без них возвращает 201 и присвоенный номер. Генерация
 *  номеров на клиенте приводила к коллизиям («Error while adding object»)
 *  и не годится, когда правят несколько человек сразу. */
export function jobPatch(j: JobEntry) {
  const attrs = j.employer.trim()
    ? [{
        _class: 'Attribute',
        type: {_class: 'AttributeType', value: 0, string: ATTR_EMPLOYER},
        value: j.employer.trim(),
        private: false,
        citation_list: [],
        note_list: [],
      }]
    : []
  return {
    _class: 'Event',
    type: {_class: 'EventType', value: EVENT_OCCUPATION, string: ''},
    description: j.profession.trim(),
    date: jobDate(j.from, j.to),
    attribute_list: attrs,
  }
}

/** Короткая запись места работы для списка: «Инженер — Кировский завод, 1955–1989».
 *  Любая часть может отсутствовать, лишние тире и запятые не рисуем. */
export function jobLabel(j: JobEntry): string {
  const what = [j.profession.trim(), j.employer.trim()].filter(Boolean).join(' — ')
  const years = j.from && j.to ? `${j.from}–${j.to}` : j.from || j.to || ''
  return [what, years].filter(Boolean).join(', ')
}
